import React, { useEffect, useState, ChangeEvent } from "react";

import {
  Switch,
  InfoPanel,
  Button,
  Modal,
  Typography,
  IconWrapper,
} from "@king-ict/eupisi-ui/components";
import { SelectEvent } from "@king-ict/eupisi-ui/components";
import { ReactComponent as WalletIcon } from "@king-ict/eupisi-ui/components/Icon/Wallet.svg";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import {
  getInstitutionBenefits,
  getIntegrationBenefitData,
  getRequestBenefitDataByRequestId,
  applyRequestBenefits,
} from "../../../api/kindergartenApi";
import { useGetRelationships } from "../../../hooks/codebook/useGetRelationships";
import useInput from "../../../hooks/input/useInput";
import BenefitFetchingParameters from "../../../models/Benefit/BenefitFetchingParameters";
import BenefitIntegrationPersonData from "../../../models/Benefit/BenefitIntegrationPersonData";
import CreateUpdateRequestBenefit from "../../../models/Request/CreateUpdateRequestBenefit";
import RequestBasicInfo from "../../../models/Request/RequestBasicInfo";
import RequestBenefit from "../../../models/Request/RequestBenefit";
import RequestBenefitData, {
  requestBenefitDataInitial,
} from "../../../models/Request/RequestBenefitData";
import { requestBenefitDetailsInitial } from "../../../models/Request/RequestBenefitDetails";
import { RequestDocumentData } from "../../../models/Request/RequestDocumentData";
import RequestStatusEnum from "../../../models/Request/RequestStatusEnum";
import { dashboard } from "../../../routes/routes";
import BenefitItem from "./BenefitItem";
import styles from "./BenefitPage.module.scss";

interface Props {
  requestBasicInfo: RequestBasicInfo;
}

export default function BenefitPage({ requestBasicInfo }: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleChange } = useInput();
  const relationships = useGetRelationships();
  const [benefitData, setBenefitData] = useState<RequestBenefitData>(
    requestBenefitDataInitial
  );
  const [institutionBenefits, setInstitutionBenefits] = useState<
    RequestBenefit[]
  >([]);

  const [allDocuments, setAllDocuments] = useState<RequestDocumentData[]>([]);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);

  const [isSent, setIsSent] = useState<boolean>(false);
  const [
    documentsModalOpenId,
    setDocumentsModalOpenId,
  ] = React.useState<number>();

  const [consentBtnVisiblity, setConsentBtnVisiblity] = React.useState<boolean>(
    true
  );

  useEffect(() => {
    (async () => {
      const institutionBenefits = await getInstitutionBenefits(
        requestBasicInfo.pedagogicalYearInstutionId
      );

      const benefits = institutionBenefits.map((ic) => {
        return {
          ...ic,
          documents: [],
          details: [],
        };
      });

      setInstitutionBenefits(benefits);

      const requestBenefits = await getRequestBenefitDataByRequestId(
        requestBasicInfo.id
      );
      setBenefitData(requestBenefits);

      if (requestBenefits !== undefined) {
        if (requestBenefits.isConsentAccepted) {
          setConsentBtnVisiblity(false);
        }
        if (requestBenefits.benefits.length > 0) {
          setIsSent(true);
        }
        const benefitDocuments: RequestDocumentData[] = [];
        requestBenefits.benefits?.forEach((requestBenefits) => {
          requestBenefits.documents?.forEach((d) => {
            benefitDocuments.push(d);
          });
        });
        setAllDocuments(benefitDocuments);
      }
    })();
  }, [requestBasicInfo.id, requestBasicInfo.pedagogicalYearInstutionId]);

  const getBenefitDocumentsById = (id: number) => {
    const benefitDocuments: RequestDocumentData[] = [];

    if (benefitData !== undefined) {
      benefitData.benefits?.forEach((requestBenefits) => {
        if (requestBenefits.institutionBenefitId === id) {
          requestBenefits.documents?.forEach((d) => {
            benefitDocuments.push(d);
          });
        }
      });
    }
    return benefitDocuments;
  };

  const checkIfBenefitExistsInRequest = (benefit: RequestBenefit) => {
    return benefitData.benefits?.some(
      (c) => c.institutionBenefitId === benefit.institutionBenefitId
    );
  };

  const onCloseDocumentsModal = () => {
    setDocumentsModalOpenId(undefined);
  };

  const handleAddMember = (institutionBenefitId: number) => {
    let newBenefitData = [] as RequestBenefit[];
    benefitData.benefits.forEach((x) => {
      if (x.institutionBenefitId === institutionBenefitId) {
        newBenefitData.push({
          ...x,
          details: [
            ...x.details,
            { ...requestBenefitDetailsInitial, id: x.details.length + 1 },
          ],
        });
      } else {
        newBenefitData.push(x);
      }
    });
    setBenefitData((prev) => {
      return { ...prev, benefits: newBenefitData };
    });
  };

  const handleRemoveMember = (institutionBenefitId: number, id: number) => {
    let newBenefitData = [] as RequestBenefit[];
    benefitData.benefits.forEach((x) => {
      if (x.institutionBenefitId === institutionBenefitId) {
        newBenefitData.push({
          ...x,
          details: x.details.filter((d) => d.id !== id),
        });
      } else {
        newBenefitData.push(x);
      }
    });
    setBenefitData((prev) => {
      return { ...prev, benefits: newBenefitData };
    });
  };

  const handleToggleSuccessModal = () => {
    setOpenSuccessModal((prevOpenSuccessModal) => !prevOpenSuccessModal);
  };

  const handleConfirmedSuccessModalClose = () => {
    handleToggleSuccessModal();
    history.push(dashboard());
  };

  const handleSelectChange = (benefit: RequestBenefit, isSelected: boolean) => {
    setBenefitData((prev) => {
      const newBenefits = isSelected
        ? prev.benefits
          ? [
              ...prev.benefits,
              {
                ...benefit,
                details: benefit.benefitTypeId
                  ? [...benefit.details, requestBenefitDetailsInitial]
                  : [...benefit.details],
              } as RequestBenefit,
            ]
          : [
              {
                ...benefit,
                details: benefit.benefitTypeId
                  ? [...benefit.details, requestBenefitDetailsInitial]
                  : [...benefit.details],
              } as RequestBenefit,
            ]
        : prev.benefits?.filter(
            (c) => c.institutionBenefitId !== benefit.institutionBenefitId
          );
      return { ...prev, benefits: newBenefits };
    });
  };

  const handleRemoveDocumentFromList = (
    benefit: RequestBenefit,
    document: RequestDocumentData
  ) => {
    const findBenefitData = benefitData.benefits.find(
      (cr) => cr.institutionBenefitId === benefit.institutionBenefitId
    );
    const newDocuments: RequestDocumentData[] = findBenefitData?.documents
      ? [...findBenefitData?.documents]
      : [];

    var index = [...newDocuments].indexOf(document);

    if (index !== -1) {
      newDocuments.splice(index, 1);

      setBenefitData((prev) => {
        const tempBenefits = prev.benefits ? [...prev.benefits] : [];
        const newBenefits: RequestBenefit[] = [];
        tempBenefits.forEach((be) => {
          if (be.institutionBenefitId === benefit.institutionBenefitId) {
            newBenefits.push({ ...be, documents: newDocuments });
          } else {
            newBenefits.push({ ...be });
          }
        });

        return { ...prev, benefits: newBenefits };
      });
    }
  };

  const handleDetailsChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
    >,
    institutionBenefitId: number,
    detailsId: number
  ) => {
    const { name } = event.target;
    const value = event.target.value;
    let newBenefitData = [] as RequestBenefit[];
    benefitData.benefits.forEach((x) => {
      if (x.institutionBenefitId === institutionBenefitId) {
        const details = x.details.map((x) => {
          if (x.id !== detailsId) {
            return x;
          } else {
            return {
              ...x,
              [name!]: value,
              integrationData: undefined,
              fetchedIntegrationData: false,
            };
          }
        });
        newBenefitData.push({
          ...x,
          details: details,
        });
      } else {
        newBenefitData.push(x);
      }
    });
    setBenefitData((prev) => {
      return { ...prev, benefits: newBenefitData };
    });
  };

  const handleDocumentsChange = (
    benefit: RequestBenefit,
    documents: RequestDocumentData[]
  ) => {
    setBenefitData((prev) => {
      const tempBenefits = prev.benefits ? [...prev.benefits] : [];
      const newBenefits: RequestBenefit[] = [];
      tempBenefits.forEach((be) => {
        if (be.institutionBenefitId === benefit.institutionBenefitId) {
          newBenefits.push({
            ...be,
            documents: be.documents
              ? [...be.documents, ...documents]
              : documents,
          });
        } else {
          newBenefits.push({ ...be });
        }
      });
      return { ...prev, benefits: newBenefits };
    });
  };

  const handleConfirmDocumentsModal = (
    benefit: RequestBenefit,
    documents: RequestDocumentData[]
  ) => {
    handleDocumentsChange(benefit, documents);
  };

  const handleGetBenefitDetailsIntegrationData = (
    data: BenefitIntegrationPersonData,
    institutionBenefitId: number,
    detailsId: number
  ) => {
    let newBenefitData = [] as RequestBenefit[];
    benefitData.benefits.forEach((x) => {
      if (x.institutionBenefitId === institutionBenefitId) {
        const details = x.details.map((x) => {
          if (x.id !== detailsId) {
            return x;
          } else {
            return {
              ...x,
              integrationData: data,
              fetchedIntegrationData: true,
            };
          }
        });
        newBenefitData.push({
          ...x,
          details: details,
        });
      } else {
        newBenefitData.push(x);
      }
    });
    setBenefitData((prev) => {
      return { ...prev, benefits: newBenefitData };
    });
  };

  const handleApplyBenefits = () => {
    let integrationBenefits = new Array<BenefitFetchingParameters>();
    let fetchedIntegration = true;

    if (benefitData.benefits) {
      fetchedIntegration =
        benefitData.benefits.filter(
          (c) => c.integrationId && !c.fetchedIntegrationData
        ).length === 0;

      if (!fetchedIntegration && benefitData.isConsentAccepted) {
        benefitData.benefits.forEach((benefit) => {
          if (benefit.integrationId) {
            integrationBenefits.push({
              benefitId: benefit.benefitId,
            } as BenefitFetchingParameters);
          }
        });
      }

      if (integrationBenefits.length > 0) {
        getIntegrationBenefitData(integrationBenefits).then((res) => {
          const newBenefits = benefitData.benefits.map((prevBenefit) => {
            const integrationResult = res.find(
              (r) => r.benefitId === prevBenefit.benefitId
            );
            if (integrationResult) {
              return {
                ...prevBenefit,
                integrationData: integrationResult,
                fetchedIntegrationData: true,
              };
            }
            return prevBenefit;
          });

          setBenefitData((prev) => ({
            ...prev,
            benefits: newBenefits,
          }));

          applyRequestBenefits(
            {
              benefitsData: benefitData.benefits,
              id: requestBasicInfo.id,
            } as CreateUpdateRequestBenefit,
            requestBasicInfo.id.toString()
          ).then(() => {
            handleToggleSuccessModal();
          });
        });

        return false;
      } else {
        applyRequestBenefits(
          {
            benefitsData: benefitData.benefits,
            id: requestBasicInfo.id,
          } as CreateUpdateRequestBenefit,
          requestBasicInfo.id.toString()
        ).then(() => {
          handleToggleSuccessModal();
        });
      }
    }
  };

  const getBenefitData = (initalBenefit: RequestBenefit) => {
    if (checkIfBenefitExistsInRequest(initalBenefit)) {
      const selectedBenefit = benefitData.benefits.find(
        (c) => c.institutionBenefitId === initalBenefit.institutionBenefitId
      );
      if (initalBenefit.integrationId) {
        if (selectedBenefit && selectedBenefit.integrationData) {
          return {
            ...initalBenefit,
            integrationData: { ...selectedBenefit.integrationData },
            fetchedIntegrationData: selectedBenefit.fetchedIntegrationData,
            details: selectedBenefit.details,
          } as RequestBenefit;
        }
      }
      if (selectedBenefit) {
        return {
          ...initalBenefit,
          details: selectedBenefit.details,
        } as RequestBenefit;
      }
    }

    return initalBenefit;
  };

  return (
    <>
      {requestBasicInfo.statusId === RequestStatusEnum.Finalized && (
        <>
          <Row>
            <Col xs={12} className={styles.bottomSpacing}>
              <InfoPanel
                message={t("benefit.mainInfoMessage")}
                color="success"
                fullWidth
              />
            </Col>
            <Col xs={12} className={styles.bottomSpacing}>
              <Switch
                name={nameof<typeof benefitData>("isConsentAccepted")}
                selected={benefitData.isConsentAccepted}
                label={t("criteriaStep.consentText")}
                onChange={(e) => handleChange(e, setBenefitData)}
                disabled={!consentBtnVisiblity || isSent}
                ariaLabel={t("criteriaStep.consentText")}
              />
            </Col>
            <Col xs={12} className={styles.bottomSpacing}>
              <hr />
            </Col>
          </Row>

          {institutionBenefits.map((c, index) => (
            <BenefitItem
              relationshipsData={relationships}
              key={c.institutionBenefitId}
              benefitTypeId={c.benefitTypeId ? c.benefitTypeId : undefined}
              consentAccepted={benefitData.isConsentAccepted}
              selected={checkIfBenefitExistsInRequest(c)}
              benefit={getBenefitData(c)}
              benefitDocuments={getBenefitDocumentsById(c.institutionBenefitId)}
              onSelect={(benefit, isSelected) =>
                handleSelectChange(benefit, isSelected)
              }
              onDocumentsChange={(benefit, documents) =>
                handleConfirmDocumentsModal(benefit, documents)
              }
              onRemoveDocumentFromList={(benefit, document) =>
                handleRemoveDocumentFromList(benefit, document)
              }
              documentsModalOpenId={documentsModalOpenId}
              handleAddMember={handleAddMember}
              handleRemoveMember={handleRemoveMember}
              setDocumentsModalOpenId={setDocumentsModalOpenId}
              onCloseDocumentModal={onCloseDocumentsModal}
              handleDetailsChange={handleDetailsChange}
              allDocuments={allDocuments}
              handleGetBenefitDetailsIntegrationData={
                handleGetBenefitDetailsIntegrationData
              }
              isSent={isSent}
              requestId={requestBasicInfo.id}
            />
          ))}
          <div className={styles.stepperButtons}>
            <Switch
              name={nameof<typeof benefitData>("isCriminalLiabilityAccepted")}
              selected={
                isSent
                  ? isSent
                  : benefitData.isCriminalLiabilityAccepted || false
              }
              label={t("benefit.accept")}
              disabled={isSent}
              onChange={(e) => handleChange(e, setBenefitData)}
              ariaLabel={t("benefit.accept")}
            />
            {!isSent && (
              <Button
                disabled={
                  !benefitData.isCriminalLiabilityAccepted ||
                  (benefitData.benefits.find((x) => x.benefitTypeId === 1) &&
                    benefitData.benefits
                      .find((x) => x.benefitTypeId === 1)
                      ?.details.some((x) => !x.fetchedIntegrationData))
                }
                onClick={handleApplyBenefits}
                size="sm"
              >
                {t("request.nextStepButton")}
              </Button>
            )}
          </div>

          <Modal
            type="message"
            open={openSuccessModal}
            confirmText={t("common.close")}
            onClose={handleConfirmedSuccessModalClose}
            onConfirm={handleConfirmedSuccessModalClose}
          >
            <div className={styles.modalMessageContent}>
              <IconWrapper color="noted-2">
              <WalletIcon />
              </IconWrapper>

              <Typography component="h1" variant="largeBold">
                {t("benefit.successModalTitle")}
              </Typography>
              {t("benefit.successModalMessage")}
            </div>
          </Modal>
        </>
      )}
    </>
  );
}
